import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeIndianRupee, ShieldCheck, ArrowRight, Check } from "lucide-react";

const POINTS = {
    pricing: ["Ex-showroom on the site", "Itemised on-road quote", "No surprise add-ons"],
    satisfaction: ["Authorized showrooms only", "Test ride before you buy", "Support till handover"],
};

const HomeGuaranteeSection = ({ pages = {} }) => {
    const eyebrow = pages.home_guarantee_eyebrow || "Our promise";
    const title = pages.home_guarantee_title || "Clear pricing. Real satisfaction.";
    const subtitle = pages.home_guarantee_sub || "What you see online is what we stand behind at every authorized Servall outlet.";

    const panels = [
        {
            icon: BadgeIndianRupee,
            accent: true,
            title: pages.home_pricing_title || "Transparent pricing",
            body: pages.home_pricing_body || "Ex-showroom prices on the site. Get a full on-road quote with RTO, insurance, and current offers — itemised before you pay. No surprise add-ons at handover.",
            points: POINTS.pricing,
            testId: "home-guarantee-pricing",
        },
        {
            icon: ShieldCheck,
            accent: false,
            title: pages.home_satisfaction_title || "Satisfaction guarantee",
            body: pages.home_satisfaction_body || "Authorized showrooms, genuine bikes, and support from first call to delivery. Test ride before you decide — and if something feels off, our team will make it right.",
            points: POINTS.satisfaction,
            testId: "home-guarantee-satisfaction",
        },
    ];

    return (
        <section
            className="relative py-12 sm:py-16 md:py-20 overflow-hidden bg-[#0A2540] text-white"
            data-testid="home-guarantee-section"
        >
            {/* Atmosphere */}
            <div className="pointer-events-none absolute inset-0" aria-hidden>
                <div className="absolute -top-24 -right-16 w-[28rem] h-[28rem] rounded-full bg-[#E31837]/25 blur-3xl" />
                <div className="absolute -bottom-32 -left-20 w-[24rem] h-[24rem] rounded-full bg-[#E31837]/15 blur-3xl" />
                <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                        backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                        backgroundSize: "48px 48px",
                    }}
                />
            </div>

            <div className="container-x relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 sm:mb-10 max-w-xl"
                >
                    <p className="text-[11px] font-semibold text-[#E31837] tracking-[0.18em] uppercase">{eyebrow}</p>
                    <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-3 leading-[1.08]">
                        {title.includes(".") ? (
                            <>
                                {title.split(".")[0]}.
                                <span className="block text-white/55 mt-1">{title.split(".").slice(1).join(".").trim()}</span>
                            </>
                        ) : (
                            title
                        )}
                    </h2>
                    <p className="text-sm sm:text-base text-white/65 mt-3 leading-relaxed">{subtitle}</p>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                    {panels.map((panel, i) => {
                        const Icon = panel.icon;
                        return (
                            <motion.div
                                key={panel.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.08 + i * 0.1, duration: 0.45 }}
                                className={`relative overflow-hidden rounded-2xl p-5 sm:p-7 border ${
                                    panel.accent
                                        ? "bg-[#E31837] border-[#E31837] shadow-[0_20px_50px_-20px_rgba(227,24,55,0.55)]"
                                        : "bg-white/[0.06] border-white/15 backdrop-blur-sm"
                                }`}
                                data-testid={panel.testId}
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl pointer-events-none ${panel.accent ? "bg-white/20" : "bg-[#E31837]/20"}`} />
                                <div className="relative">
                                    <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-4 ${panel.accent ? "bg-white/20 text-white" : "bg-[#E31837] text-white"}`}>
                                        <Icon size={26} strokeWidth={1.75} />
                                    </div>
                                    <h3 className="font-display font-bold text-xl sm:text-2xl leading-tight">{panel.title}</h3>
                                    <p className={`text-sm mt-2.5 leading-relaxed ${panel.accent ? "text-white/90" : "text-white/70"}`}>{panel.body}</p>
                                    <ul className="mt-5 space-y-2.5">
                                        {panel.points.map((pt) => (
                                            <li key={pt} className="flex items-center gap-2.5 text-sm font-medium">
                                                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${panel.accent ? "bg-white text-[#E31837]" : "bg-[#E31837] text-white"}`}>
                                                    <Check size={12} strokeWidth={3} />
                                                </span>
                                                {pt}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.25 }}
                    className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5"
                >
                    <Link
                        to="/contact"
                        className="inline-flex items-center justify-center gap-2 bg-white text-[#0A2540] font-bold text-xs uppercase tracking-wide px-7 py-3.5 rounded-xl hover:bg-[#E31837] hover:text-white transition-colors min-h-[48px]"
                        data-testid="home-guarantee-cta"
                    >
                        Get today&apos;s on-road quote <ArrowRight size={15} />
                    </Link>
                    <Link
                        to="/dealers"
                        className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-white/80 hover:text-white min-h-[44px]"
                    >
                        Visit a showroom <ArrowRight size={14} />
                    </Link>
                </motion.div>
            </div>
        </section>
    );
};

export default HomeGuaranteeSection;
